import type { JournalTrade } from '../types'
import { blocksToPlainText } from '@/lib/blockText'

/**
 * The summary tiles, from the rows the table is showing — ROADMAP 9.53.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COMPUTED FROM THE ROWS, WHICH IS WHY IT IS A FUNCTION OF THEM
 *
 * The tiles and the table must never disagree, so the tiles are derived from
 * exactly what is on screen rather than from a second query. That is also
 * why the page reads the whole history and pages its RENDERING — see the
 * server's `journalFor`.
 *
 * Extracted from the screen because it is arithmetic a trader judges
 * themselves by, and arithmetic in a `useMemo` cannot be tested without a
 * DOM. A trade that broke exactly even is neither a win nor a loss and is
 * counted in neither; rounding it into one would quietly inflate the rate.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface JournalSummary {
  net: number
  gross: number
  costs: number
  wins: number
  losses: number
  winRate: number | null
  avgHold: number | null
  unreviewed: number
  /** How many rows are booked-but-unfinished, so a mixed total can say so. */
  openCount: number
}

export function summarise(rows: readonly JournalTrade[]): JournalSummary {
    /*
     * ── WHAT THE TILES COUNT, AFTER 9.53 ──────────────────────────────────
     *
     * The page now shows partially booked positions, which are not over.
     * MONEY counts them and JUDGEMENTS do not, and the split is not a
     * compromise — it is the difference between the two kinds of question:
     *
     *   net / gross / charges   what has this account actually banked? A
     *                           booked lot is banked whether or not the
     *                           rest of the position is still running.
     *   win rate / avg hold     how did my finished trades turn out? A
     *                           position still open has not turned out at
     *                           all, and counting it would score a trade
     *                           that has not happened yet — in the half
     *                           that happens to be in profit so far.
     *
     * `unreviewed` follows the judgement side: there is nothing to review
     * about a trade that is not finished.
     * ──────────────────────────────────────────────────────────────────────
     */
    const done = rows.filter((t) => t.completion !== 'PARTIAL')
    const net = rows.reduce((a, t) => a + t.net, 0)
    const gross = rows.reduce((a, t) => a + t.gross, 0)
    const costs = rows.reduce((a, t) => a + t.chargesPaid, 0)
    const wins = done.filter((t) => t.net > 0).length
    const losses = done.filter((t) => t.net < 0).length
    const decided = wins + losses
    const held = done.filter((t) => t.holdMinutes !== null)
    return {
      net,
      gross,
      costs,
      wins,
      losses,
      winRate: decided === 0 ? null : Math.round((wins / decided) * 100),
      avgHold:
        held.length === 0
          ? null
          : Math.round(held.reduce((a, t) => a + (t.holdMinutes ?? 0), 0) / held.length),
      unreviewed: done.filter((t) => !t.reviewedAt && t.journalTags.length === 0 && !blocksToPlainText(t.journalNote).trim()).length,
      /** Shown beside the money tiles so a mixed total says it is mixed. */
      openCount: rows.length - done.length,
    }
}
